import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from "firebase/firestore";
import { db } from '../firebase';

function Subcontractors() {
  const [subcontractors, setSubcontractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(null);
  const [editingSub, setEditingSub] = useState(null);
  const [editName, setEditName] = useState('');
  const [editRate, setEditRate] = useState('');
  const [saving, setSaving] = useState(false);
  const [newSubName, setNewSubName] = useState('');
  const [newSubRate, setNewSubRate] = useState('');
  const [projectsData, setProjectsData] = useState({});

  useEffect(() => {
    fetchSubcontractors();
  }, []);

  const fetchSubcontractors = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, "subcontractors"));
      const subcontractorsList = [];
      querySnapshot.forEach((doc) => {
        subcontractorsList.push({ id: doc.id, ...doc.data() });
      });
      setSubcontractors(subcontractorsList);
      
      // Fetch all projects to calculate total days
      const projectsSnapshot = await getDocs(collection(db, "projects"));
      const projectsInfo = {};
      
      projectsSnapshot.forEach((doc) => {
        const project = doc.data();
        if (project.labour) {
          project.labour.forEach((labour) => {
            const subId = labour.subcontractor.id;
            if (!projectsInfo[subId]) {
              projectsInfo[subId] = {
                totalDays: 0,
                totalEarnings: 0,
                projects: []
              };
            }
            projectsInfo[subId].totalDays += Number(labour.days);
            projectsInfo[subId].totalEarnings += Number(labour.cost);
            projectsInfo[subId].projects.push({
              name: project.name,
              days: labour.days,
              earnings: labour.cost
            });
          });
        }
      });
      
      setProjectsData(projectsInfo);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const addSubcontractor = async () => {
    if (!newSubName.trim() || !newSubRate) return;

    try {
      setLoading(true);
      await addDoc(collection(db, "subcontractors"), {
        name: newSubName.trim(),
        dayRate: Number(newSubRate)
      });
      setNewSubName('');
      setNewSubRate('');
      setShowAddForm(false);
      await fetchSubcontractors();
    } catch (error) {
      console.error("Error adding subcontractor:", error);
      alert('Failed to add subcontractor');
    } finally {
      setLoading(false);
    }
  };

  const openEditForm = (sub, e) => {
    if (e) e.stopPropagation();
    setEditingSub(sub);
    setEditName(sub.name || '');
    setEditRate(sub.dayRate != null ? String(sub.dayRate) : '');
    setShowDetailsModal(null);
  };

  const closeEditForm = () => {
    setEditingSub(null);
    setEditName('');
    setEditRate('');
  };

  const updateSubcontractor = async () => {
    if (!editingSub) return;
    if (!editName.trim()) {
      alert('Please enter a subcontractor name');
      return;
    }
    if (editRate === '' || Number.isNaN(Number(editRate))) {
      alert('Please enter a valid day rate');
      return;
    }

    try {
      setSaving(true);
      await updateDoc(doc(db, "subcontractors", editingSub.id), {
        name: editName.trim(),
        dayRate: Number(editRate),
        updatedAt: new Date().toISOString(),
      });
      closeEditForm();
      await fetchSubcontractors();
    } catch (error) {
      console.error("Error updating subcontractor:", error);
      alert('Failed to update subcontractor');
    } finally {
      setSaving(false);
    }
  };

  const deleteSubcontractor = async (id) => {
    if (window.confirm('Are you sure you want to delete this subcontractor?')) {
      try {
        setLoading(true);
        await deleteDoc(doc(db, "subcontractors", id));
        await fetchSubcontractors();
      } catch (error) {
        console.error("Error deleting subcontractor:", error);
        alert('Failed to delete subcontractor');
      } finally {
        setLoading(false);
      }
    }
  };

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Loading...</div>;
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '15px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <Link to="/dashboard" className="home-button" style={{
            padding: '8px 16px',
            backgroundColor: '#f0f2f5',
            color: '#2c3e50',
            textDecoration: 'none',
            borderRadius: '6px',
            border: '1px solid #e1e5ea',
            fontWeight: 500,
            transition: 'all 0.2s ease',
            whiteSpace: 'nowrap'
          }}>
            ← Home
          </Link>
          <h1>Subcontractors</h1>
        </div>
        <button
          onClick={() => setShowAddForm(true)}
          style={{
            padding: '10px 20px',
            backgroundColor: '#27ae60',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            boxShadow: '0 2px 4px rgba(39, 174, 96, 0.2)'
          }}
          onMouseOver={(e) => {
            e.target.style.backgroundColor = '#219a52';
            e.target.style.boxShadow = '0 4px 8px rgba(39, 174, 96, 0.3)';
          }}
          onMouseOut={(e) => {
            e.target.style.backgroundColor = '#27ae60';
            e.target.style.boxShadow = '0 2px 4px rgba(39, 174, 96, 0.2)';
          }}
        >
          Add New Subcontractor
        </button>
      </div>

      <div style={{
        display: 'grid',
        gap: '20px',
        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))'
      }}>
        {subcontractors.map(sub => {
          const subData = projectsData[sub.id] || { totalDays: 0, totalEarnings: 0 };
          return (
            <div
              key={sub.id}
              onClick={() => setShowDetailsModal(sub)}
              style={{
                backgroundColor: 'white',
                padding: '20px',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                ':hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                }
              }}
            >
              <h3 style={{ margin: '0 0 10px 0' }}>{sub.name}</h3>
              <p style={{ margin: '0 0 10px 0', color: '#666' }}>
                Day Rate: £{sub.dayRate}
              </p>
              <p style={{ margin: '0 0 15px 0', color: '#666' }}>
                Total Days: {subData.totalDays}
              </p>
              <p style={{ margin: '0 0 15px 0', color: '#2c3e50', fontWeight: 'bold' }}>
                Total Earnings: £{subData.totalEarnings}
              </p>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                <button
                  onClick={(e) => openEditForm(sub, e)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#e8f5fe',
                    color: '#2AA4F7',
                    border: '1px solid #b6e0fb',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: 600
                  }}
                >
                  Edit
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteSubcontractor(sub.id);
                  }}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#ffecec',
                    color: '#dc3545',
                    border: '1px solid #f5c6cb',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {showAddForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '400px'
          }}>
            <h2 style={{ marginTop: 0 }}>Add New Subcontractor</h2>
            <input
              type="text"
              value={newSubName}
              onChange={(e) => setNewSubName(e.target.value)}
              placeholder="Subcontractor Name"
              style={{
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid rgba(42,164,247,0.3)',
                width: '100%',
                marginBottom: '10px',
                ':focus': {
                  outline: 'none',
                  borderColor: '#2AA4F7',
                  boxShadow: '0 0 0 2px rgba(42,164,247,0.2)'
                }
              }}
            />
            <input
              type="number"
              value={newSubRate}
              onChange={(e) => setNewSubRate(e.target.value)}
              placeholder="Day Rate"
              style={{
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid rgba(42,164,247,0.3)',
                width: '100%',
                marginBottom: '20px',
                ':focus': {
                  outline: 'none',
                  borderColor: '#2AA4F7',
                  boxShadow: '0 0 0 2px rgba(42,164,247,0.2)'
                }
              }}
            />
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowAddForm(false)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={addSubcontractor}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#27ae60',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Add
              </button>
            </div>
          </div>
        </div>
      )}

      {showDetailsModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h2 style={{ margin: 0 }}>{showDetailsModal.name}</h2>
              <button
                onClick={() => setShowDetailsModal(null)}
                style={{
                  padding: '5px 10px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '1.5rem'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <p><strong>Day Rate:</strong> £{showDetailsModal.dayRate}</p>
              <p><strong>Total Days Worked:</strong> {projectsData[showDetailsModal.id]?.totalDays || 0}</p>
              <p><strong>Total Earnings:</strong> £{projectsData[showDetailsModal.id]?.totalEarnings || 0}</p>
              <button
                onClick={() => openEditForm(showDetailsModal)}
                style={{
                  marginTop: '8px',
                  padding: '8px 16px',
                  backgroundColor: '#2AA4F7',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 600
                }}
              >
                Edit Details
              </button>
            </div>

            <h3>Project History</h3>
            {projectsData[showDetailsModal.id]?.projects.length > 0 ? (
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: '10px' 
              }}>
                {projectsData[showDetailsModal.id].projects.map((project, index) => (
                  <div 
                    key={index}
                    style={{
                      padding: '15px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '4px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <span><strong>{project.name}</strong></span>
                    <div>
                      <span style={{ marginRight: '15px' }}>{project.days} days</span>
                      <span>£{project.earnings}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ textAlign: 'center', color: '#666' }}>
                No projects yet
              </p>
            )}
          </div>
        </div>
      )}

      {editingSub && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '400px'
          }}>
            <h2 style={{ marginTop: 0 }}>Edit Subcontractor</h2>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500, color: '#2c3e50' }}>
              Name
            </label>
            <input
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              placeholder="Subcontractor Name"
              style={{
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid rgba(42,164,247,0.3)',
                width: '100%',
                marginBottom: '10px',
                boxSizing: 'border-box'
              }}
            />
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500, color: '#2c3e50' }}>
              Day Rate (£)
            </label>
            <input
              type="number"
              step="0.01"
              value={editRate}
              onChange={(e) => setEditRate(e.target.value)}
              placeholder="Day Rate"
              style={{
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid rgba(42,164,247,0.3)',
                width: '100%',
                marginBottom: '20px',
                boxSizing: 'border-box'
              }}
            />
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={closeEditForm}
                disabled={saving}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={updateSubcontractor}
                disabled={saving}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#2AA4F7',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  opacity: saving ? 0.7 : 1
                }}
              >
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Subcontractors; 